import {useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import {createStructuredSelector} from 'reselect';

import uamTimeouts from '../../../../consts/config/uamTimeouts';
import * as dataCollectionTypes from '../../../../consts/uam/dataCollectionTypes';
import useDidUpdate from '../../../../hooks/useDidUpdate';
import * as dataCollectionService from '../../../../services/uam/dataCollectionService';
import uamClientService from '../../../../services/uam/uamClientService';
import advanceDiagnosticService from '../../../../services/yap/advanceDiagnosticService';
import yapService from '../../../../services/yap/yapService';
import {hideLoader, showLoader} from '../../../../state/ducks/global';
import {setIsYapDataCollectionAllowed} from '../../../../state/ducks/yapEncrypted';
import {
    selectDiagnosticFlowDataResult,
    selectIsDiagnosticFlow,
    selectIsDiagnosticFlowDataCollectionInProgress,
} from '../../../../state/selectors/advancedDiagnostic';
import {makeSelectIotDeviceMergedWithConsumerProduct} from '../../../../state/selectors/consumer';
import {selectIsStartAutoSync} from '../../../../state/selectors/global';
import {
    selectDeviceActivationStatus,
    selectIsDeviceReady,
    selectIsFirmwareInProgress,
} from '../../../../state/selectors/iotDevice';
import {
    makeSelectIsDeviceSyncInProgress,
    makeSelectIsYapActivationInProgress,
    makeSelectIsYapAutoCleaningInProgress,
    makeSelectIsYapDeviceInfoReceived,
    makeSelectIsYapFindDeviceInProgress,
    makeSelectIsYapFirmwareInProgress,
    makeSelectIsYapHolderInfoReceived,
    makeSelectIsYapSynchronizeDataCollectionReceived,
    makeSelectIsYapUpdateSettingsInProgress,
    makeSelectIsYapUsageFlow,
    makeSelectIsYapUsageFlowDataCollectionInProgress,
    makeSelectIsYapUsageFlowDataCollectionReceived,
    makeSelectYapCancelDataCollectionInProgress,
    makeSelectYapSynchronizeDataCollectionData,
} from '../../../../state/selectors/yapEncrypted';
import {dispatch} from '../../../../state/store';

const mapStateToProps = createStructuredSelector({
    isYapDeviceInfoReceived: makeSelectIsYapDeviceInfoReceived(),
    isYapHolderInfoReceived: makeSelectIsYapHolderInfoReceived(),
    iotProduct: makeSelectIotDeviceMergedWithConsumerProduct(),
    isDeviceReady: selectIsDeviceReady,
    isDeviceSyncInProgress: makeSelectIsDeviceSyncInProgress(),
    isHolderSyncInProgress: makeSelectIsDeviceSyncInProgress(true),
    isAutoCleaningInProgress: makeSelectIsYapAutoCleaningInProgress(),
    isFindDeviceInProgress: makeSelectIsYapFindDeviceInProgress(),
    isYapUpdateSettingsInProgress: makeSelectIsYapUpdateSettingsInProgress(),
    isYapHolderUpdateSettingsInProgress: makeSelectIsYapUpdateSettingsInProgress(true),
    isYapActivationInProgress: makeSelectIsYapActivationInProgress(),
    isYapFirmwareInProgress: makeSelectIsYapFirmwareInProgress(),
    isDeviceActivated: selectDeviceActivationStatus,
    isYapSynchronizeDeviceDataCollectionReceived: makeSelectIsYapSynchronizeDataCollectionReceived(),
    isYapUsageFlowDeviceDataCollectionReceived: makeSelectIsYapUsageFlowDataCollectionReceived(),
    isYapUsageFlow: makeSelectIsYapUsageFlow(),
    isDiagnosticFlow: selectIsDiagnosticFlow,
    isFirmwareInProgress: selectIsFirmwareInProgress,
    yapSynchronizeDataCollectionData: makeSelectYapSynchronizeDataCollectionData(),
    isYapUsageFlowDataCollectionInProgress: makeSelectIsYapUsageFlowDataCollectionInProgress(),
    isDiagnosticFlowDataCollectionInProgress: selectIsDiagnosticFlowDataCollectionInProgress,
    isYapSynchronizeHolderDataCollectionReceived: makeSelectIsYapSynchronizeDataCollectionReceived(true),
    isYapUsageFlowHolderDataCollectionReceived: makeSelectIsYapUsageFlowDataCollectionReceived(true),
    diagnosticFlowDataResult: selectDiagnosticFlowDataResult,
    isStartAutoSync: selectIsStartAutoSync,
    isCancelDataCollectionInProgress: makeSelectYapCancelDataCollectionInProgress(),
});

const DataCollectionBaseHandler = (props) => {
    const {
        dataCollectionType,
        isYapUsageFlow,
        yapSynchronizeDataCollectionData,
        diagnosticFlowDataResult,
        isYapUsageFlowDataCollectionInProgress,
        isDiagnosticFlow,
        isDiagnosticFlowDataCollectionInProgress,
        isYapSynchronizeDeviceDataCollectionReceived,
        isYapSynchronizeHolderDataCollectionReceived,
        isYapUsageFlowDeviceDataCollectionReceived,
        isYapUsageFlowHolderDataCollectionReceived,
        isStartAutoSync,
        isCancelDataCollectionInProgress,
        ...rest
    } = props;
    const isDataCollectionEnabled = yapService.isDataCollectionEnabled();
    const dataCollectionDelayTimer = useRef(false);

    const isGlobal = dataCollectionType === dataCollectionTypes.DATA_COLLECTION_GLOBAL;
    const isUsage = dataCollectionType === dataCollectionTypes.DATA_COLLECTION_USAGE;
    const isDiagnostic = dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC;

    const {isYapSynchronizeDataCollectionInProgress, isYapSynchronizeDataCollectionIsHolder} =
        yapSynchronizeDataCollectionData || {};
    const isInProgress =
        isDiagnosticFlowDataCollectionInProgress ||
        isYapUsageFlowDataCollectionInProgress ||
        isYapSynchronizeDataCollectionInProgress;
    const {isDeviceReceived, isHolderReceived} = diagnosticFlowDataResult || {};
    const isDiagnosticFinished = advanceDiagnosticService.isDiagnosticFinished(diagnosticFlowDataResult);

    const dataCollectionTypesToStart = [
        {
            type: dataCollectionTypes.DATA_COLLECTION_GLOBAL,
            isActive:
                !isYapUsageFlow &&
                !isYapUsageFlowDataCollectionInProgress &&
                !isDiagnosticFlow &&
                !isDiagnosticFlowDataCollectionInProgress &&
                isDataCollectionEnabled,
            isDeviceDataCollectionReceived: isYapSynchronizeDeviceDataCollectionReceived,
            isHolderDataCollectionReceived: isYapSynchronizeHolderDataCollectionReceived,
            timeout: uamTimeouts.UAM_DATA_COLLECTION_BEFORE_START_TIMEOUT,
        },
        {
            type: dataCollectionTypes.DATA_COLLECTION_USAGE,
            isActive: isYapUsageFlow,
            isDeviceDataCollectionReceived: isYapUsageFlowDeviceDataCollectionReceived,
            isHolderDataCollectionReceived: isYapUsageFlowHolderDataCollectionReceived,
            timeout: 0,
        },
        {
            type: dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC,
            isActive: isDiagnosticFlow,
            isDeviceDataCollectionReceived: isDiagnosticFinished || isDeviceReceived,
            isHolderDataCollectionReceived: isDiagnosticFinished || isHolderReceived,
            timeout: 0,
        },
    ];
    const dataCollectionTypeData = dataCollectionTypesToStart.find((x) => x.type === dataCollectionType);
    const isCanBeStarted = dataCollectionTypeData.isActive && isStartAutoSync;

    const {isStartDataCollection, isHolder} = dataCollectionService.isDataCollectionCanBeStarted({
        dataCollectionTypeData,
        isUsage,
        isDiagnostic,
        isCanBeStarted,
        isInProgress,
        ...rest,
    });

    const isShouldBeStopped = dataCollectionService.isDataCollectionShouldBeStopped({
        dataCollectionTypeData,
        isGlobal,
        isUsage,
        isDiagnostic,
        isInProgress,
        isHolder: isYapSynchronizeDataCollectionIsHolder,
        isCanBeStarted,
        isYapSynchronizeDataCollectionInProgress,
        isYapUsageFlowDataCollectionInProgress,
        isDiagnosticFlowDataCollectionInProgress,
        ...rest,
    });

    const isShowLoader =
        (isDiagnosticFlowDataCollectionInProgress || isYapUsageFlowDataCollectionInProgress) &&
        isCancelDataCollectionInProgress;

    useEffect(() => {
        if (isShowLoader) {
            dispatch(showLoader());
        } else {
            dispatch(hideLoader());
        }
    }, [isShowLoader]);

    useDidUpdate(async () => {
        clearTimeout(dataCollectionDelayTimer.current);

        if (!isStartDataCollection) return;

        dataCollectionDelayTimer.current = setTimeout(async () => {
            dispatch(setIsYapDataCollectionAllowed(true));
            await uamClientService.proceedSynchronizeAllData(isHolder, false, dataCollectionType);
        }, dataCollectionTypeData.timeout * 1000);
    }, [isStartDataCollection]);

    useDidUpdate(async () => {
        if (!isShouldBeStopped) return;

        clearTimeout(dataCollectionDelayTimer.current);

        dispatch(setIsYapDataCollectionAllowed(false));
        await uamClientService.cancelSynchronizeData({dataCollectionType});
    }, [isShouldBeStopped]);

    return null;
};

export default connect(mapStateToProps)(DataCollectionBaseHandler);
